import {
	AccountActivationFormData,
	ActivationResponse,
	ActivationUserData,
	EAccountActivationEmptyFields,
} from "@data-types";
import { localizationService } from "@state";
import { getCurrentCulture, isNullOrWhitespace } from "@utils";
import { getRedirectOriginUrl } from "@utils/specific/capacitor";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, observable, runInAction } from "mobx";
import { PlayerPromoSendingModeEnum } from "@api-types/user/AccountActivationRequestDto";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { UserAuthStore } from "@state/shared";
import { UserData } from "./types";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const  AccountActivationService = lazy(loadFailPath, ()=>import("@services/membership/account-activation/AccountActivationService"));

export class AccountActivationStore {
	userStore: UserAuthStore;

	@observable public isAccountActivationWorking: boolean = false;
	@observable public accountActivationData?: ActivationResponse | null = null;
	@observable
	public emptyFields: EAccountActivationEmptyFields =
		EAccountActivationEmptyFields.NONE;
	@observable public accountActivationError: boolean = false;
	@observable public isAccountActivationCongratulationsActive: boolean =
		false;

	constructor(userStore: UserAuthStore) {
		this.userStore = userStore;
	}

	@action.bound
	public async onInitializeAccountActivation(): Promise<void> {
		if (this.userStore.accountActivation == null) return;

		this.isAccountActivationWorking = true;

		const response = await (
			await AccountActivationService
		).default.getAccountActivationData(
			this.userStore.accountActivation?.activationToken,
			this.userStore.accountActivation?.agencyId
		);

		if (response.result != null) {
			runInAction(() => (this.accountActivationData = response.result));
		} else if (response?.error != null) {
			console.error(response.error);
		}

		runInAction(() => {
			this.isAccountActivationWorking = false;
		});
	}

	@action.bound
	public async onAccountActivationSubmit(
		data: AccountActivationFormData
	): Promise<void> {
		if (this.userStore.accountActivation == null) {
			return;
		}

		this.isAccountActivationWorking = true;
		this.emptyFields = EAccountActivationEmptyFields.NONE;

		const activationModel = {
			activationToken: this.userStore.accountActivation.activationToken,
			agencyId: this.userStore.accountActivation.agencyId,
			agencyKey: this.userStore.accountActivation.agencyKey,
			shopId: this.userStore.accountActivation.shopId,
			email: data.email,
			emailChangeUrl: getRedirectOriginUrl(
				`email-activation?mailkey={mailkey}&username={username}`
			),
			password: data.password,
			username: data.userCardNumber,
			depositLimit: Number(data.depositLimit),
			playerPromoSendingMode: data.isReceivingPromoEmailsEnabled
				? PlayerPromoSendingModeEnum.enabled
				: PlayerPromoSendingModeEnum.disabled,
		};

		const response = await (
			await AccountActivationService
		).default.createAccountActivation(activationModel);

		if (response.result != null) {
			this.userStore.loginStore.resolveUserActivationLogin(
				response.result.primary,
				response.result.secondary,
				Number(response.result.errorCode)
			);
			runInAction(() => {
				this.isAccountActivationCongratulationsActive = true;
			});
		} else if (response.error != null) {
			const error = response.error;
			const notifyError =
				this.userStore.rootStore.notificationStore.error;

			if (error?.userData) {
				if (error.userData?.activationToken) {
					//show popup with user data
					// @ts-expect-error
					this.setUserDataToBeConfirmed({
						...error.userData,
						isInternetUser: false,
					});
					this.setUserConfirmationData(error.userData);
					this.userStore.loginStore.setUserDataConfirmationState(
						true
					);
				} else {
					if (error.userData.shopInfo) {
						//show message about shop
						notifyError(
							localizationService.t(
								"USER.POPUPS.DATA_CONFIRMATION.USER_DATA_CONFIRMATION_REQUIRED",
								{
									0: error.userData.shopInfo,
								}
							)
						);
					} else {
						notifyError(
							localizationService.t(
								"MEMBERSHIP.ACCOUNT_ACTIVATION.ERROR_HAS_OCCURED"
							)
						);
					}
				}
			} else {
				notifyError(
					localizationService.t(
						`MEMBERSHIP.ACCOUNT_ACTIVATION.${error?.message}`
					)
				);
			}
		}
		runInAction(() => {
			this.isAccountActivationWorking = false;
		});
	}

	@action.bound
	public async onAccountActivationFormSubmit(
		data: AccountActivationFormData
	): Promise<void> {
		if (
			!this.accountActivationData?.userInfo?.isOfflineUserMailApproved &&
			isNullOrWhitespace(data.email) &&
			!this.accountActivationData?.companyInfo
				.isOfflineUserPasswordChangeMandatory &&
			isNullOrWhitespace(data.password)
		) {
			this.emptyFields = EAccountActivationEmptyFields.MAIL_AND_PASSWORD;
		} else if (
			!this.accountActivationData?.userInfo?.isOfflineUserMailApproved &&
			isNullOrWhitespace(data.email)
		) {
			this.emptyFields = EAccountActivationEmptyFields.MAIL;
		} else if (
			!this.accountActivationData?.companyInfo
				.isOfflineUserPasswordChangeMandatory &&
			isNullOrWhitespace(data.password)
		) {
			this.emptyFields = EAccountActivationEmptyFields.PASSWORD;
		} else {
			await this.onAccountActivationSubmit(data);
		}
	}

	@action.bound
	public async onCancelAccountActivation(): Promise<void> {
		this.isAccountActivationWorking = true;

		this.userStore.logoutUser();

		runInAction(() => (this.isAccountActivationWorking = false));
	}

	@action.bound
	public clearAccountActivationError(): void {
		this.accountActivationError = false;
	}

	@action.bound
	public closeEmptyFieldsPopup(): void {
		this.emptyFields = EAccountActivationEmptyFields.NONE;
	}

	@action.bound
	public closeAccountActivationCongratulations(): void {
		this.isAccountActivationCongratulationsActive = false;
	}

	@action.bound
	public setUserConfirmationData(
		userData: ActivationUserData | UserData | null
	) {
		if (userData != null) {
			this.userStore.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.CONFIRMATION_DATA,
				JSON.stringify(userData)
			);
		} else {
			this.userStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.CONFIRMATION_DATA
			);
		}
	}

	@action.bound
	setConfirmationDataState() {
		this.userStore.userDataConfirmationStore.setUserDataToBeConfirmed(
			JSON.parse(
				this.userStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.CONFIRMATION_DATA
				) as string
			) as UserData
		);
	}

	@action.bound
	public onDisposeAccountActivation(): void {
		this.accountActivationData = null;
		this.emptyFields = EAccountActivationEmptyFields.NONE;
		this.clearAccountActivationError();
	}

	public onDisposeAccountActivationCongratulations(): void {
		this.isAccountActivationCongratulationsActive = false;
	}
}
